// utils/storyblokApi.ts
import { getStoryblokApi, StoryblokClient } from '@storyblok/react'
import { PostListItem, Meta } from '@/domains/post'
import { Topic } from '@/domains/topic'

import { adaptStoryblokToPost, adaptStoryblokPagination } from '@/helpers/storyblock'

// Types for API parameters
interface BlogPostsParams {
  page?: number
  perPage?: number
  tag?: string
  searchQuery?: string
  locale?: string
  version?: 'draft' | 'published'
  sortBy?: string
}

interface RelatedPostsParams {
  tags: string[]
  excludeSlug: string
  maxPosts?: number
  locale?: string
  version?: 'draft' | 'published'
}

interface SinglePostParams {
  slug: string
  locale?: string
  version?: 'draft' | 'published'
  resolveLinks?: boolean
  resolveRelations?: string[]
}

interface TagsParams {
  locale?: string
  version?: 'draft' | 'published'
}

// Utility function to get common Storyblok parameters
const getBaseStoryblokParams = (locale?: string, version?: 'draft' | 'published') => {
  const params: any = {
    version: version || getStoryblokVersion(),
    starts_with: 'blog/',
  }

  if (locale && locale !== 'en') {
    params.language = locale
  }

  return params
}

/**
 * Fetch blog posts with filtering, pagination, and search
 */
export const fetchBlogPosts = async ({
  page = 1,
  perPage = 6,
  tag,
  searchQuery,
  locale,
  version,
  sortBy = 'content.custom_date:desc',
}: BlogPostsParams = {}): Promise<{
  posts: PostListItem[]
  pagination: Meta['pagination']
  total: number
}> => {
  try {
    const storyblokApi = getStoryblokApi()

    const sbParams: any = {
      ...getBaseStoryblokParams(locale, version),
      is_startpage: false,
      per_page: perPage,
      page,
      sort_by: sortBy,
    }

    // Add tag filtering if provided
    if (tag) {
      sbParams.with_tag = tag
    }

    // Add search functionality
    if (searchQuery) {
      sbParams.search_term = searchQuery
    }

    const { data: postsData, total } = await storyblokApi.get('cdn/stories', sbParams)

    const posts = postsData.stories.map(adaptStoryblokToPost)
    const pagination = adaptStoryblokPagination(total, perPage, page)

    return {
      posts,
      pagination,
      total,
    }
  } catch (error) {
    console.error('Error fetching blog posts:', error)
    throw new Error('Failed to fetch blog posts')
  }
}

/**
 * Fetch related posts based on tags
 */
export const fetchRelatedPosts = async ({
  tags,
  excludeSlug,
  maxPosts = 3,
  locale,
  version,
}: RelatedPostsParams): Promise<PostListItem[]> => {
  try {
    if (!tags.length) {
      return []
    }

    const storyblokApi = getStoryblokApi()

    const sbParams = {
      ...getBaseStoryblokParams(locale, version),
      per_page: maxPosts * 2, // Fetch more to ensure we have enough after filtering
      excluding_slugs: `blog/${excludeSlug}`,
      with_tag: tags.join(','),
      sort_by: 'published_at:desc',
    }

    const { data } = await storyblokApi.get('cdn/stories', sbParams)

    if (!data?.stories) {
      return []
    }

    return data.stories.map(adaptStoryblokToPost).slice(0, maxPosts)
  } catch (error) {
    console.error('Error fetching related posts:', error)
    throw new Error('Failed to fetch related posts')
  }
}

/**
 * Fetch a single blog post by slug
 */
export const fetchBlogPost = async ({
  slug,
  locale,
  version,
  resolveLinks = true,
  resolveRelations = [],
}: SinglePostParams): Promise<any> => {
  try {
    const storyblokApi = getStoryblokApi()

    const sbParams: any = {
      version: version || getStoryblokVersion(),
    }

    if (resolveLinks) {
      sbParams.resolve_links = 'url'
    }

    if (resolveRelations.length > 0) {
      sbParams.resolve_relations = resolveRelations
    }

    if (locale && locale !== 'en') {
      sbParams.language = locale
    }

    const { data } = await storyblokApi.get(`cdn/stories/blog/${slug}`, sbParams)

    if (!data?.story) {
      return null
    }

    return data.story
  } catch (error) {
    console.error('Error fetching blog post:', error)
    throw new Error('Failed to fetch blog post')
  }
}

/**
 * Fetch all blog post slugs for static generation
 */
export const fetchBlogSlugs = async (locale?: string, version?: 'draft' | 'published'): Promise<string[]> => {
  try {
    const storyblokApi = getStoryblokApi()

    const sbParams = {
      ...getBaseStoryblokParams(locale, version),
      is_startpage: false,
      per_page: 100, // Adjust based on your needs
    }

    const { data } = await storyblokApi.get('cdn/stories', sbParams)

    return data.stories.map((story: any) => story.slug.replace('blog/', ''))
  } catch (error) {
    console.error('Error fetching blog slugs:', error)
    throw new Error('Failed to fetch blog slugs')
  }
}

/**
 * Fetch tags/topics from Storyblok
 */
export const fetchBlogTags = async ({ locale, version }: TagsParams = {}): Promise<Topic[]> => {
  try {
    const storyblokApi = getStoryblokApi()

    const sbParams = {
      version: version || getStoryblokVersion(),
      starts_with: 'blog/',
      language: locale === 'en' ? undefined : locale,
    }

    const { data: tagsData } = await storyblokApi.get('cdn/tags', sbParams)

    return tagsData.tags.map((tag: any) => ({
      id: tag.name,
      attributes: {
        name: tag.name,
        slug: tag.name.toLowerCase().replace(/\s+/g, '-'),
        postsCount: tag.count || 0,
      },
    }))
  } catch (error) {
    console.error('Error fetching blog tags:', error)
    throw new Error('Failed to fetch blog tags')
  }
}

/**
 * Batch fetch blog data (posts + tags) for initial page load
 */
export const fetchBlogPageData = async ({
  page = 1,
  perPage = 12,
  tag,
  searchQuery,
  locale,
  version,
}: BlogPostsParams = {}): Promise<{
  posts: PostListItem[]
  topics: Topic[]
  pagination: Meta['pagination']
}> => {
  try {
    const [postsResult, topics] = await Promise.all([
      fetchBlogPosts({ page, perPage, tag, searchQuery, locale, version }),
      fetchBlogTags({ locale }),
    ])

    return {
      posts: postsResult.posts,
      topics,
      pagination: postsResult.pagination,
    }
  } catch (error) {
    console.error('Error fetching blog page data:', error)
    throw new Error('Failed to fetch blog page data')
  }
}

/**
 * Fetch blog post with related posts
 */
export const fetchBlogPostWithRelated = async ({
  slug,
  locale,
  version,
  maxRelatedPosts = 3,
}: {
  slug: string
  locale?: string
  version?: 'draft' | 'published'
  maxRelatedPosts?: number
}): Promise<{
  story: any
  relatedPosts: PostListItem[]
}> => {
  try {
    // Fetch the main blog post
    const story = await fetchBlogPost({
      slug,
      locale,
      version,
      resolveLinks: true,
      resolveRelations: ['author'],
    })

    if (!story) {
      throw new Error('Blog post not found')
    }

    // Fetch related posts if the story has tags
    let relatedPosts: PostListItem[] = []
    if (story.tag_list && story.tag_list.length > 0) {
      relatedPosts = await fetchRelatedPosts({
        tags: story.tag_list,
        excludeSlug: slug,
        maxPosts: maxRelatedPosts,
        locale,
        version,
      })
    }

    return {
      story,
      relatedPosts,
    }
  } catch (error) {
    console.error('Error fetching blog post with related:', error)
    throw new Error('Failed to fetch blog post with related posts')
  }
}

// Environment-aware version getter
export const getStoryblokVersion = (): 'draft' | 'published' => {
  const envVersion = process.env.NEXT_STORYBLOK_VERSION
  return envVersion === 'production' ? 'published' : 'draft'
}

// Higher-order function for error handling
export const withErrorHandling = <T extends any[], R>(fn: (...args: T) => Promise<R>, fallbackValue: R) => {
  return async (...args: T): Promise<R> => {
    try {
      return await fn(...args)
    } catch (error) {
      console.error(`API call failed:`, error)
      return fallbackValue
    }
  }
}
